import API from '@/services/api'
import clientId from '@/services/clientId'
import { SEND_LOGS } from '@/services/enpoints'

const ALLOWED_EVENTS = new Set([
  'cart.add.started',
  'cart.add.request_sent',
  'cart.add.succeeded',
  'cart.add.rejected',
  'cart.add.network_error',
  'cart.add.timeout',
  'cart.add.unexpected_error'
])
const MAX_BATCH_EVENTS = 20
const MAX_BODY_BYTES = 32 * 1024
const RETRY_DELAY_MS = 30000

let queue = []
let flushTimer = null
let flushing = false

function scheduleFlush (delay = 250) {
  if (flushTimer || queue.length === 0) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    flush().catch(() => {})
  }, delay)
}

function normalizeEvent (data) {
  if (!data || !ALLOWED_EVENTS.has(data.event)) return null

  const event = {
    event_id: data.event_id || clientId.generateUuidV4(),
    event: data.event,
    occurred_at: data.occurred_at || new Date().toISOString(),
    client_id: data.client_id || clientId.getClientId(),
    interaction_id: data.interaction_id,
    request_id: data.request_id,
    endpoint: data.endpoint,
    duration_ms: data.duration_ms,
    http_status: data.http_status,
    error_kind: data.error_kind,
    cart_id: data.cart_id,
    product_id: data.product_id,
    quantity: data.quantity,
    frontend_version: data.frontend_version || process.env.VUE_APP_COMMIT_SHA || process.env.VUE_APP_VERSION || 'local',
  }

  return Object.keys(event).reduce((result, key) => {
    if (event[key] !== undefined && event[key] !== null) result[key] = event[key]
    return result
  }, {})
}

function bodySize (events) {
  return new Blob([JSON.stringify({ events })]).size
}

function takeBatch () {
  const batch = []

  while (queue.length && batch.length < MAX_BATCH_EVENTS) {
    const candidate = queue[0]
    if (batch.length && bodySize([...batch, candidate]) > MAX_BODY_BYTES) break

    queue.shift()
    batch.push(candidate)
  }

  return batch
}

function shouldRetry (error) {
  const status = error.response && error.response.status
  return !error.response || [502, 503, 504].includes(status)
}

async function flush () {
  if (flushing || queue.length === 0) return

  const batch = takeBatch()
  if (batch.length === 0) return

  flushing = true
  try {
    await API.call({
      method: 'POST',
      url: SEND_LOGS,
      data: { events: batch },
      timeout: 10000,
      interactionId: batch[0].interaction_id,
    })
  } catch (error) {
    if (shouldRetry(error)) {
      queue = batch.concat(queue)
      scheduleFlush(RETRY_DELAY_MS)
    }
  } finally {
    flushing = false
  }

  if (queue.length && !flushTimer) scheduleFlush()
}

function track (data) {
  const event = normalizeEvent(data)
  if (!event || bodySize([event]) > MAX_BODY_BYTES) return null

  queue.push(event)
  scheduleFlush()
  return event.event_id
}

// Compatibilidade com os call sites legados: objetos Axios brutos não são
// enviados. Somente eventos normalizados e permitidos pelo contrato entram na fila.
function logs (data) {
  if (data && data.event) return Promise.resolve(track(data))
  return Promise.resolve(null)
}

export const logsService = {
  logs,
  track,
  flush,
}
